import { DiscoverFeedPage } from './features/discover/discover-page'
import { CurationPage } from './redux-toolkits/home-page-v2.1/homePageV21.type'
import { lazy, Suspense } from 'react'
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom'
import { PageNotFound } from './features/404'
import ForgotPasswordPage from './features/auth/forgot-password'
import LoginPage from './features/auth/login'
import ResetPasswordPage from './features/auth/reset-password'
import { DashboardPage } from './features/dashboard'
import { OrdersPage } from './features/orders'
import NoneProtectedRoute from './route/NoneProtectedRoute'
import ProtectedRoute from './route/ProtectedRoute'

import { SingleEssentialPage } from './features/essentials/singleEssential'
import OrderDetailsPage from './features/orders/OrderDetailsPage'
import Settings from './features/settings'
import Transactions from './features/transactions/TransactionsPage'
import Vendors from './features/vendors'

import 'react-day-picker/style.css'
import 'react-loading-skeleton/dist/skeleton.css'
import ErrorBoundary from './components/errorBoundary/errorBoundary'
import Spinner from './components/spinner/Spinner'
import AuditLog from './features/audit-log'
import { GoodHomePage } from './features/channel-curation/good-home'
import { GoodHomeV21Page } from './features/channel-curation/good-home-v2.1'
import { SingleSectionPage } from './features/channel-curation/good-home/section'
import { NavMenuPage } from './features/channel-curation/nav-menu/NavMenuPage'
import { SingleNavMenuPage } from './features/channel-curation/nav-menu/SingleNavMenuPage'
import { CustomersPage } from './features/customers/customersPage'
import { SingleCustomerPage } from './features/customers/singleCustomerPage'
import { DiscountPage } from './features/discounts'
import { EarningPartnersPage } from './features/earningPartners'
import { EssentialsPage } from './features/essentials'
import { ManageEssentialDetails } from './features/essentials/manageEssentialDetails'
import { EventsPage } from './features/events'
import { SingleEventPage } from './features/events/singleEvent'
import Feedback from './features/feedback'
import { FulfilmentCenterPage } from './features/fulfilment-centers'
import { SingleFulfilmentCenterPage } from './features/fulfilment-centers/singleFulfilmentCenter'
import { ListArticles } from './features/good-lists/ListArticles'
import { SingleListArticle } from './features/good-lists/SingleListArticle'
import { InterestFormPage } from './features/interests/InterestFormPage'
import { ShoppingPreferencesPage } from './features/shopping-preferences/shopping-preferences-page'
import {
  LEGACY_SHOPPING_PREFERENCES_ROUTE,
  SHOPPING_PREFERENCES_ROUTE,
} from './features/shopping-preferences/shopping-preferences.constant'
import PlatformCostMetricsPage from './features/platform-cost-metrics/PlatformCostMetricsPage'
import { POSPage } from './features/pos-module'
import { Products } from './features/products'
import { BulkPriceEditPage } from './features/products/bulk-price-edit-page'
import { CreateProductPage } from './features/products/CreateProduct'
import { ReferralsPage } from './features/referrals'
import { InitiateRefundPage } from './features/refund/initiate'
import RefundsPage from './features/refund/refundsPage'
import { SingleOrderRefundPage } from './features/refund/singleOrderRefund'
import { SingleSalesRepPage } from './features/sales-reps/singleSalesRepPage'
import { SettlementPage } from './features/settlement/settlementPage'
import { SingleSettlementPage } from './features/settlement/singleSettlementPage'
import { SingleStakeholderPage } from './features/stakeholder/SingleStakeholderPage'
import { SingleTransactionPage } from './features/transactions/singleTransactionPage'
import { CreatorCurationPage } from './features/vendors/creatorCurationPage'
import { InviteVendorPage } from './features/vendors/inviteVendorPage'
import { SingleVendorPage } from './features/vendors/singleVendorPage'
import { VouchersPage } from './features/vouchers'
import { usePermissions } from './hooks/usePermissions'
import { INTEREST_ROUTE } from './redux-toolkits/interests/interests.constant'
import { PLATFORM_COST_METRICS_ROUTE } from './redux-toolkits/platform-cost-metrics/platform-cost-metrics.constant'

const InterestOrderPage = lazy(async () => {
  const module = await import('./features/interests/InterestOrderPage')
  return { default: module.InterestOrderPage }
})

const App = () => {
  const { hasPermissions } = usePermissions()
  const GoodHomeRoute =
    process.env.REACT_APP_GOOD_HOME_VERSION === '2.1'
      ? GoodHomeV21Page
      : GoodHomePage
  const isGoodHomeV21Enabled = process.env.REACT_APP_GOOD_HOME_VERSION === '2.1'

  return (
    <Router>
      <ErrorBoundary>
        <Routes>
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<DashboardPage />} />

            <Route path="/dashboard" element={<DashboardPage />} />

            {hasPermissions(['view_homepage_section']) ? (
              <>
                <Route
                  path="/channel-curation"
                  element={<GoodHomeRoute key="home" />}
                />
                <Route
                  path="/channel-curation/browse"
                  element={
                    <GoodHomeV21Page key="browse" page={CurationPage.BROWSE} />
                  }
                />
              </>
            ) : undefined}

            {hasPermissions(['view_homepage_section']) ? (
              <Route
                path="/channel-curation/nav-menu"
                element={<NavMenuPage />}
              />
            ) : undefined}

            {hasPermissions(['view_homepage_section']) ? (
              <Route
                path="/channel-curation/nav-menu/new"
                element={<SingleNavMenuPage mode="create" />}
              />
            ) : undefined}

            {hasPermissions(['view_homepage_section']) ? (
              <Route
                path="/channel-curation/nav-menu/:id"
                element={<SingleNavMenuPage mode="edit" />}
              />
            ) : undefined}

            {isGoodHomeV21Enabled &&
            hasPermissions(['view_homepage_section']) ? (
              <Route
                path="/channel-curation/v2.1"
                element={<GoodHomeV21Page />}
              />
            ) : undefined}

            {hasPermissions([
              'create_homepage_section',
              'update_homepage_section',
            ]) ? (
              <Route
                path="/channel-curation/section/:id"
                element={<SingleSectionPage />}
              />
            ) : undefined}

            {hasPermissions(['view_order']) ? (
              <Route path="/orders" element={<OrdersPage />} />
            ) : undefined}

            {hasPermissions(['view_order']) ? (
              <Route
                path="/orders/:harmonizedReference"
                element={<OrderDetailsPage />}
              />
            ) : undefined}

            {hasPermissions(['create_refund']) ? (
              <Route
                path="/orders/:harmonizedReference/:orderId/initiate-refund"
                element={<InitiateRefundPage />}
              />
            ) : undefined}

            {hasPermissions(['view_vendor']) ? (
              <Route path="/creators" element={<Vendors />} />
            ) : undefined}

            {hasPermissions(['invite_vendor']) ? (
              <Route path="/creators/invite" element={<InviteVendorPage />} />
            ) : undefined}

            {hasPermissions(['view_vendor', 'update_vendor']) ? (
              <Route
                path="/creators/curation"
                element={<CreatorCurationPage />}
              />
            ) : undefined}

            {hasPermissions(['view_vendor']) ? (
              <Route path="/creators/:id" element={<SingleVendorPage />} />
            ) : undefined}

            {hasPermissions(['view_fulfillment_centres']) ? (
              <Route
                path="/fulfilment-center"
                element={<FulfilmentCenterPage />}
              />
            ) : undefined}

            {hasPermissions(['view_fulfillment_centres']) ? (
              <Route
                path="/fulfilment-center/:id"
                element={<SingleFulfilmentCenterPage />}
              />
            ) : undefined}

            {hasPermissions(['view_pos']) ? (
              <Route path="/pos" element={<POSPage />} />
            ) : undefined}

            {hasPermissions(['view_transactions']) ? (
              <Route path="/transactions" element={<Transactions />} />
            ) : undefined}

            {hasPermissions(['view_transaction']) ? (
              <Route
                path="/transactions/:id"
                element={<SingleTransactionPage />}
              />
            ) : undefined}

            {/* <Route path="/waitlist" element={<WaitListPage />} /> */}

            {hasPermissions(['view_product']) ? (
              <Route path="/products" element={<Products />} />
            ) : undefined}

            {hasPermissions(['view_product', 'update_product']) ? (
              <Route
                path="/products/bulk-price-edit"
                element={<BulkPriceEditPage />}
              />
            ) : undefined}

            {hasPermissions(['add_product']) ? (
              <Route
                path="/products/new/:type"
                element={<CreateProductPage />}
              />
            ) : undefined}

            {hasPermissions(['view_product', 'update_product']) ? (
              <Route
                path="/products/view/:id/"
                element={<CreateProductPage />}
              />
            ) : undefined}

            {hasPermissions(['view_article']) ? (
              <Route path="/lists/articles" element={<ListArticles />} />
            ) : undefined}

            {hasPermissions(['view_article', 'update_article']) ? (
              <Route
                path="/lists/articles/view/:id"
                element={<SingleListArticle mode="edit" />}
              />
            ) : undefined}

            {hasPermissions(['create_article']) ? (
              <Route
                path="/lists/articles/create"
                element={<SingleListArticle mode="create" />}
              />
            ) : undefined}

            {hasPermissions(['view_list']) ? (
              <Route path="/lists/curated" element={<EssentialsPage />} />
            ) : undefined}

            {hasPermissions(['create_list']) ? (
              <Route
                path="/lists/curated/new"
                element={<ManageEssentialDetails />}
              />
            ) : undefined}

            {hasPermissions(['view_list', 'update_list']) ? (
              <Route
                path="/lists/curated/edit/:id"
                element={<ManageEssentialDetails />}
              />
            ) : undefined}

            {hasPermissions(['view_list']) ? (
              <Route
                path="/lists/curated/view/:id"
                element={<SingleEssentialPage />}
              />
            ) : undefined}

            {(hasPermissions(['update_system_settings']) ||
              hasPermissions(['export_reports'])) && (
              <Route path="/discover" element={<DiscoverFeedPage />} />
            )}
            {hasPermissions(['view_discounts']) ? (
              <Route path="/discounts" element={<DiscountPage />} />
            ) : undefined}

            {hasPermissions(['view_discounts']) ? (
              <Route path="/vouchers" element={<VouchersPage />} />
            ) : undefined}

            {hasPermissions(['view_referrals']) ? (
              <Route path="/referrals" element={<ReferralsPage />} />
            ) : undefined}

            {hasPermissions(['update_system_settings']) ? (
              <Route path="/settings" element={<Settings />} />
            ) : undefined}

            {hasPermissions(['view_settlements']) ? (
              <Route path="/settlement" element={<SettlementPage />} />
            ) : undefined}

            {hasPermissions(['view_settlements']) ? (
              <Route
                path="/settlement/:id"
                element={<SingleSettlementPage />}
              />
            ) : undefined}

            {hasPermissions(['view_customers']) ? (
              <Route path="/customers" element={<CustomersPage />} />
            ) : undefined}

            {hasPermissions(['view_customers']) ? (
              <>
                <Route
                  path={SHOPPING_PREFERENCES_ROUTE.TAB}
                  element={<ShoppingPreferencesPage />}
                />
                <Route
                  path={SHOPPING_PREFERENCES_ROUTE.BASE}
                  element={
                    <Navigate
                      to={SHOPPING_PREFERENCES_ROUTE.SPENDING_QUESTIONS}
                      replace
                    />
                  }
                />
                <Route
                  path={LEGACY_SHOPPING_PREFERENCES_ROUTE.SPENDING_QUESTIONS}
                  element={
                    <Navigate
                      to={SHOPPING_PREFERENCES_ROUTE.SPENDING_QUESTIONS}
                      replace
                    />
                  }
                />
                <Route
                  path={LEGACY_SHOPPING_PREFERENCES_ROUTE.INTERESTS}
                  element={
                    <Navigate
                      to={SHOPPING_PREFERENCES_ROUTE.INTERESTS}
                      replace
                    />
                  }
                />
              </>
            ) : undefined}

            {hasPermissions(['view_customers']) ? (
              <Route
                path={INTEREST_ROUTE.CREATE}
                element={<InterestFormPage />}
              />
            ) : undefined}

            {hasPermissions(['view_customers']) ? (
              <Route
                path={INTEREST_ROUTE.EDIT}
                element={<InterestFormPage />}
              />
            ) : undefined}

            {hasPermissions(['view_customers']) ? (
              <Route
                path={INTEREST_ROUTE.ORDER}
                element={
                  <Suspense fallback={<Spinner />}>
                    <InterestOrderPage />
                  </Suspense>
                }
              />
            ) : undefined}

            {hasPermissions(['view_customers']) ? (
              <Route path="/customers/:id" element={<SingleCustomerPage />} />
            ) : undefined}

            {hasPermissions(['view_refunds']) ? (
              <Route path="/refunds" element={<RefundsPage />} />
            ) : undefined}

            {hasPermissions(['view_refunds']) ? (
              <Route
                path="/refunds/:orderId"
                element={<SingleOrderRefundPage />}
              />
            ) : undefined}

            {hasPermissions(['view_revenue_metrics']) ? (
              <Route
                path={PLATFORM_COST_METRICS_ROUTE}
                element={<PlatformCostMetricsPage />}
              />
            ) : undefined}

            <Route
              path="/stakeholder/:id"
              element={<SingleStakeholderPage />}
            />

            {hasPermissions(['view_customers_feedback']) ? (
              <Route path="/feedback" element={<Feedback />} />
            ) : undefined}

            {hasPermissions(['add_product']) ? (
              <Route
                path="/earning-partners"
                element={<EarningPartnersPage />}
              />
            ) : undefined}

            {hasPermissions(['view_audits_logs']) ? (
              <Route path="/audit-log" element={<AuditLog />} />
            ) : undefined}

            {hasPermissions(['add_product']) ? (
              <Route path="/events" element={<EventsPage />} />
            ) : undefined}

            {hasPermissions(['add_product']) ? (
              <Route path="/events/:id" element={<SingleEventPage />} />
            ) : undefined}

            {hasPermissions(['view_dashboard']) ? (
              <Route path="/sales-rep/:id" element={<SingleSalesRepPage />} />
            ) : undefined}

            <Route path="*" element={<PageNotFound />} />
          </Route>

          <Route element={<NoneProtectedRoute />}>
            <Route path="/auth/login" element={<LoginPage />} />

            <Route
              path="/auth/forgot-password"
              element={<ForgotPasswordPage />}
            />

            <Route
              path="/auth/reset-password"
              element={<ResetPasswordPage />}
            />
          </Route>

          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </ErrorBoundary>
    </Router>
  )
}

export default App
